// Piezas visuales de las páginas de eventos: textos con cursiva, polaroids, pulseras, boletos y las capas animadas
// (estrellas, fuegos artificiales, focos, confeti, nieve, niebla, murciélagos y destellos). Sin lógica de negocio.
import React, { useId, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { eventos } from '../../data/eventos';
import { nombreEvento, precargarEvento, rutaEvento } from '../../services/eventos';
import useTira from '../../hooks/useTira';
import { mini } from '../../utils';

/** @typedef {import('../../data/eventos').Evento} Evento */
/** @typedef {import('../../data/eventos').TextoConEnfasis} TextoConEnfasis */
/** @typedef {import('../../data/eventos').FotoEvento} FotoEvento */
/** @typedef {import('../../data/eventos').MasFotos} MasFotos */

/**
 * Números pseudoaleatorios con semilla: la decoración queda siempre en el mismo lugar.
 * @param {number} semilla
 * @returns {() => number}
 */
export function azar(semilla) {
  let a = semilla >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * Texto en el que las palabras con guion no se parten al final del renglón ("Not-So-Scary" se queda junta).
 * @param {{ texto: string }} props
 */
export function SinCortes({ texto }) {
  return <>{texto.split(/(\S*-\S*)/).map((parte, i) => (parte.includes('-') ? <span key={i} className="ev-sin-corte">{parte}</span> : parte))}</>;
}

/**
 * Texto con su parte en cursiva, como los títulos del sitio.
 * @param {{ texto: TextoConEnfasis, claseEm?: string }} props
 */
export function Enfasis({ texto, claseEm = '' }) {
  return <><SinCortes texto={texto.antes} /><em className={claseEm}><SinCortes texto={texto.em} /></em><SinCortes texto={texto.despues || ''} /></>;
}

/**
 * Nombre oficial completo de un evento.
 * @param {{ evento: Evento }} props
 */
export function NombreEvento({ evento }) {
  return <SinCortes texto={nombreEvento(evento)} />;
}

/**
 * Props para descargar de antemano la portada de un evento cuando alguien está por abrirlo: el mouse encima, el foco
 * del teclado o el dedo sobre el enlace.
 * @param {Evento} ev
 * @returns {{ onMouseEnter: () => void, onFocus: () => void, onTouchStart: () => void }}
 */
export const alIntentar = (ev) => {
  const precargar = () => precargarEvento(ev);
  return { onMouseEnter: precargar, onFocus: precargar, onTouchStart: precargar };
};

/**
 * Cuándo se pide una foto: `primero` (la principal de la portada, con prioridad alta), `visible` (también en la
 * portada, sin esperar), `diferida` (más abajo: se pide cuando se acerca a la pantalla) o `pendiente` (su sección
 * todavía está lejos: se dibuja el hueco gris y la foto aún no se pide).
 * @typedef {'primero'|'visible'|'diferida'|'pendiente'} CargaFoto
 */

/** GIF transparente de 1 px: ocupa el lugar de una foto `pendiente` sin pedir nada (se ve el fondo gris del hueco). */
const FOTO_PENDIENTE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';

/**
 * Polaroid con cinta, como las del resto del sitio (con la versión de 560 px de la foto).
 * @param {{ foto: FotoEvento, carga?: CargaFoto, className?: string }} props
 */
export function Polaroid({ foto, carga = 'diferida', className = '' }) {
  return (
    <figure className={`polaroid ev-polaroid ev-polaroid--${foto.forma} ${className}`}>
      <span className="ev-cinta" aria-hidden="true" />
      <img src={carga === 'pendiente' ? FOTO_PENDIENTE : mini(foto.src)} alt={foto.pie} loading={carga === 'diferida' || carga === 'pendiente' ? 'lazy' : 'eager'} fetchpriority={carga === 'primero' ? 'high' : 'auto'}
        decoding="async" style={foto.encuadre ? { objectPosition: foto.encuadre } : undefined} />
      <figcaption>{foto.pie}</figcaption>
    </figure>
  );
}

/** Giro de cada polaroid de la tira, en orden (se repite). */
const GIROS_TIRA = [-2.5, 2, -1.5, 2.5, -2];

/**
 * Tira de polaroids que se desliza de lado, debajo del collage de "Lo que viví": con el dedo en el celular y con
 * flechas desde 900 px. Las fotos (versión de 560 px) se piden hasta que van a verse. Si todas caben, no hay flechas.
 * En los extremos la flecha se apaga con aria-disabled (no con disabled) para que quien usa el teclado no pierda el foco.
 * @param {{ masFotos: MasFotos, id: string, carga?: CargaFoto }} props  id: nombre único de la pista, para enlazarla
 *   con sus flechas; carga: `pendiente` mientras su sección está lejos
 */
export function TiraFotos({ masFotos, id, carga = 'diferida' }) {
  const pista = useRef(null);
  const { alInicio, alFinal, mover } = useTira(pista);
  const idPista = `${id}-mas-fotos`;
  return (
    <div className="ev-tira">
      <div className="ev-tira__cabeza">
        <p className="ev-tira__titulo">{masFotos.titulo}</p>
        {!(alInicio && alFinal) && (
          <div className="ev-tira__flechas">
            <button type="button" className="ev-tira__flecha" aria-label="Fotos anteriores" aria-controls={idPista} aria-disabled={alInicio} onClick={() => { if (!alInicio) mover(-1); }}>
              <i className="fa-solid fa-arrow-left" aria-hidden="true" />
            </button>
            <button type="button" className="ev-tira__flecha" aria-label="Más fotos" aria-controls={idPista} aria-disabled={alFinal} onClick={() => { if (!alFinal) mover(1); }}>
              <i className="fa-solid fa-arrow-right" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
      <div id={idPista} ref={pista} className="ev-tira__pista" role="region" aria-label={masFotos.titulo} tabIndex={0}>
        {masFotos.fotos.map((f, i) => (
          <div key={f.src} className={`ev-tira__foto ev-tira__foto--${f.forma}`} style={{ '--giro': `${GIROS_TIRA[i % GIROS_TIRA.length]}deg` }}>
            <Polaroid foto={f} carga={carga} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Pieza de un collage: posición y ancho en % de su caja, en celular (m) y en escritorio (d), con su giro.
 * @typedef {Object} Lugar
 * @property {[number, number, number, number]} d   [x %, y %, ancho %, giro°] en escritorio
 * @property {[number, number, number, number]} m   lo mismo en celular
 */

/**
 * @param {{ lugar: Lugar, flota?: boolean, retraso?: number, className?: string, children: import('react').ReactNode }} props
 */
export function Pieza({ lugar, flota = false, retraso = 0, className = '', children }) {
  const [x, y, w, g] = lugar.d;
  const [xm, ym, wm, gm] = lugar.m;
  const estilo = {
    '--x': `${x}%`, '--y': `${y}%`, '--w': `${w}%`, '--giro-d': `${g}deg`,
    '--xm': `${xm}%`, '--ym': `${ym}%`, '--wm': `${wm}%`, '--giro-m': `${gm}deg`,
    animationDelay: `${retraso}s`
  };
  return <div className={`ev-pieza${flota ? ' ev-flota' : ''} ${className}`} style={estilo}>{children}</div>;
}

/**
 * Pulsera de un evento (el motivo de estas páginas): banda de su color con broche, su nombre completo y el año.
 * Con `enlace` lleva a la página del evento. Repite otros enlaces de la misma página (boletos, calendario o lista), así
 * que no entra al recorrido con tabulador ni al lector de pantalla.
 * @param {{ evento: Evento, className?: string, style?: import('react').CSSProperties, enlace?: boolean }} props
 */
export function Pulsera({ evento, className = '', style, enlace = false }) {
  const t = evento.tema;
  const estilo = { '--banda': t.banda, '--broche': t.broche, '--banda-texto': t.bandaTexto, ...style };
  const partes = (
    <>
      <span className="ev-pulsera__broche" />
      <span className="ev-pulsera__corte" />
      <span className="ev-pulsera__nombre"><NombreEvento evento={evento} /></span>
      <span className="ev-pulsera__anio">{evento.anio}</span>
    </>
  );
  if (!enlace) return <div className={`ev-pulsera ${className}`} aria-hidden="true" style={estilo}>{partes}</div>;
  return (
    <Link to={rutaEvento(evento)} className={`ev-pulsera ev-pulsera--enlace ${className}`} aria-hidden="true" tabIndex={-1} style={estilo} {...alIntentar(evento)}>
      {partes}
    </Link>
  );
}

/**
 * Escalonado (px) y giro (°) de cada pulsera de la pila, en escritorio (dx, giro) y en celular (dxm, girom).
 * @type {{ dx: number[], giro: number[], dxm: number[], girom: number[] }}
 */
const PILA = { dx: [0, 40, 12, 52, 24], giro: [-3, -1, -2, 0, -1], dxm: [0, 22, 6, 28, 12], girom: [-2, -1, -1, 0, -1] };

/**
 * Las 5 pulseras en una pila ordenada: una por renglón, con un escalonado pequeño y giros leves, y cada una se mece
 * apenas desde su broche. Así se leen completos el nombre y el año de cada una. Cada pulsera lleva a su evento.
 */
export function PilaPulseras() {
  return (
    <div className="ev-pila">
      {eventos.map((ev, i) => (
        <Pulsera key={ev.id} evento={ev} enlace className="ev-mece" style={{
          '--dx': `${PILA.dx[i]}px`, '--dxm': `${PILA.dxm[i]}px`, '--giro-d': `${PILA.giro[i]}deg`, '--giro-m': `${PILA.girom[i]}deg`,
          animationDelay: `-${i * 0.15}s`
        }} />
      ))}
    </div>
  );
}

/**
 * Boleto de un evento: foto, corte de boleto y datos. Lleva a la página del evento.
 * @param {{ evento: Evento, grande?: boolean }} props
 */
export function Boleto({ evento, grande: esGrande = false }) {
  const t = evento.tema;
  return (
    <Link to={rutaEvento(evento)} className={`ev-boleto${esGrande ? ' ev-boleto--grande' : ''}${t.oscuro ? ' ev-boleto--noche' : ''}`}
      style={{ '--ev-fondo': t.fondo, '--ev-texto': t.texto, '--ev-suave': t.suave, '--ev-acento': t.oscuro ? t.acento : t.oscuroAcento, '--ev-em': t.acento }}
      {...alIntentar(evento)}>
      <span className="ev-boleto__foto">
        <img src={mini(evento.portada.src)} alt="" loading="lazy" decoding="async" style={{ objectPosition: evento.portada.encuadre }} />
        {esGrande && <span className="ev-boleto__chip">{evento.parque} · {evento.anio}</span>}
      </span>
      <span className="ev-boleto__corte" aria-hidden="true" />
      <span className="ev-boleto__cuerpo">
        <span className="ev-boleto__nombre"><Enfasis texto={evento.titulo} /></span>
        {esGrande
          ? <span className="ev-boleto__cuando"><i className="fa-regular fa-calendar" />{evento.cuando}</span>
          : <span className="ev-boleto__meta">{evento.parque} · {evento.anio}</span>}
        <span className="ev-boleto__ver">Ver la fiesta<i className="fa-solid fa-arrow-right" /></span>
      </span>
    </Link>
  );
}

// ---------- figuras decorativas ----------
export const Murcielago = () => (
  <svg className="ev-aletea" width="64" height="28" viewBox="0 0 64 28" aria-hidden="true">
    <path d="M32 8C34 5 36 5 37 8C40 3 48 1 56 4C52 6 50 9 50 12C54 11 59 12 64 15C58 15 55 18 54 22C51 19 47 19 45 22C43 19 39 18 37 20C36 17 34 16 32 16C30 16 28 17 27 20C25 18 21 19 19 22C17 19 13 19 10 22C9 18 6 15 0 15C5 12 10 11 14 12C14 9 12 6 8 4C16 1 24 3 27 8C28 5 30 5 32 8Z" fill="#0A0612" />
  </svg>
);
/** @param {{ tam: number }} props */
export const Calabaza = ({ tam }) => (
  <svg width={tam} height={Math.round(tam * 0.9)} viewBox="0 0 40 36" aria-hidden="true">
    <ellipse cx="20" cy="21" rx="17" ry="13" fill="#F7931E" />
    <ellipse cx="20" cy="21" rx="7.5" ry="13" fill="#E47C0F" />
    <path d="M20 8C20 5 22 3 25 3" stroke="#3F6B2A" strokeWidth="2.6" fill="none" strokeLinecap="round" />
    <path d="M11 18l4-3.4 2 4.2zM29 18l-4-3.4-2 4.2z" fill="#2A1640" />
    <path d="M11.5 24.5q8.5 6.5 17 0q-3 4.6-8.5 4.6t-8.5-4.6z" fill="#2A1640" />
  </svg>
);
/** @param {{ tam: number }} props */
const Fantasma = ({ tam }) => (
  <svg width={tam} height={Math.round(tam * 1.2)} viewBox="0 0 30 36" aria-hidden="true">
    <path d="M15 2C8 2 4 7 4 14V33l4-3 3.5 3 3.5-3 3.5 3 3.5-3 4 3V14C26 7 22 2 15 2Z" fill="#FFFFFF" />
    <circle cx="11" cy="15" r="2" fill="#2A1640" />
    <circle cx="19" cy="15" r="2" fill="#2A1640" />
  </svg>
);
/** @param {{ tam: number }} props */
const Caramelo = ({ tam }) => (
  <svg width={tam} height={Math.round(tam * 1.2)} viewBox="0 0 20 24" aria-hidden="true">
    <path d="M10 1L19 22Q10 25 1 22Z" fill="#FFFFFF" />
    <path d="M5.6 11.2h8.8L19 22Q10 25 1 22Z" fill="#F7931E" />
    <path d="M3.2 16.8h13.6L19 22Q10 25 1 22Z" fill="#FFD166" />
  </svg>
);
/** @param {{ color: string, tam: number }} props */
export const Globo = ({ color, tam }) => (
  <svg width={tam} height={Math.round(tam * 2.25)} viewBox="0 0 40 90" aria-hidden="true">
    <path d="M20 45C24 58 16 70 20 88" stroke="rgba(19,33,43,.4)" strokeWidth="1.2" fill="none" />
    <ellipse cx="20" cy="22" rx="16" ry="20" fill={color} />
    <path d="M17 41l3 4 3-4z" fill={color} />
    <ellipse cx="14" cy="15" rx="4" ry="6.5" fill="rgba(255,255,255,.35)" />
  </svg>
);
/** Colores de la lámpara: blanca con contorno de tinta, sombra cálida y luz amarilla. */
const LAMPARA = { tinta: '#13212B', blanco: '#FFFFFF', sombra: '#E6DFD2', boca: '#FFD166', foco: '#FFF3C4', luz: '#FFE9A8', contorno: 2.2 };
/** Resorte del brazo de abajo: zigzag paralelo al brazo, del lado de afuera. */
const RESORTE_LAMPARA = 'M77.6 78L76.3 74.4L81.8 73L78.1 68.7L83.6 67.3L80 62.9L85.5 61.5L81.9 57.2L85.1 55';

/**
 * Lámpara de escritorio que brinca junto al título de Pixar Fest, voltea a ver "Fest" y lo ilumina.
 * Mira a la izquierda, hacia el título. Las partes que se mueven llevan su clase (ev-lampara__cuerpo,
 * __cabeza, __sombra, __haz y __luz) y su animación está en Eventos.css; el haz se sale del dibujo y
 * queda detrás del texto porque la lámpara va en el fondo del título.
 */
export function Lampara() {
  const id = useId().replace(/:/g, '');
  const { tinta, blanco, sombra, boca, foco, luz, contorno } = LAMPARA;
  /** @type {React.SVGProps<SVGElement>} */
  const borde = { stroke: tinta, strokeWidth: contorno, strokeLinejoin: 'round' };
  return (
    <svg viewBox="0 0 120 120" aria-hidden="true">
      <defs>
        <radialGradient id={`${id}-halo`}>
          <stop offset="0" stopColor={luz} stopOpacity=".9" />
          <stop offset="1" stopColor={luz} stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${id}-haz`} gradientUnits="userSpaceOnUse" x1="0" y1="38" x2="0" y2="470">
          <stop offset="0" stopColor={boca} stopOpacity=".78" />
          <stop offset=".5" stopColor={boca} stopOpacity=".32" />
          <stop offset="1" stopColor={boca} stopOpacity="0" />
        </linearGradient>
      </defs>
      <ellipse className="ev-lampara__sombra" cx="80" cy="115" rx="33" ry="4.5" fill={tinta} fillOpacity=".16" />
      <g className="ev-lampara__cuerpo">
        <path d="M86 93L100.5 46.5M92 49.2L47 23.2" stroke={tinta} strokeWidth="3" strokeLinecap="round" />
        <path d="M80 90L95 44L50 18" stroke={tinta} strokeWidth={7 + contorno * 2} strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <path d="M80 90L95 44L50 18" stroke={blanco} strokeWidth="7" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <path d={RESORTE_LAMPARA} stroke={tinta} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <rect x="51" y="108" width="58" height="7" rx="3.5" fill={blanco} {...borde} />
        <path d="M57 109C57 97 67.5 90 80 90C92.5 90 103 97 103 109Z" fill={blanco} />
        <path d="M91 92C98.5 94.5 103 101 103 109H96.5C96.5 102 94.5 96.5 91 92Z" fill={sombra} />
        <path d="M57 109C57 97 67.5 90 80 90C92.5 90 103 97 103 109Z" fill="none" {...borde} />
        <circle cx="80" cy="90" r="5" fill={blanco} {...borde} />
        <circle cx="95" cy="44" r="6.5" fill={blanco} {...borde} />
        <circle cx="95" cy="44" r="2" fill={tinta} />
        <g className="ev-lampara__cabeza">
          <g transform="translate(50 18) rotate(40)">
            <path className="ev-lampara__haz" d="M-22 38L22 38L60 470L-150 470Z" fill={`url(#${id}-haz)`} />
            <circle className="ev-lampara__luz" cx="0" cy="46" r="30" fill={`url(#${id}-halo)`} />
            <rect x="-8" y="-7" width="16" height="13" rx="4" fill={blanco} {...borde} />
            <path d="M-10 4H10L28 38H-28Z" fill={blanco} />
            <path d="M4 4H10L28 38H17Z" fill={sombra} />
            <path d="M-10 4H10L28 38H-28Z" fill="none" {...borde} />
            <g className="ev-lampara__luz">
              <ellipse cx="0" cy="38" rx="28" ry="6.5" fill={boca} {...borde} />
              <path d="M-11 39A11 11 0 0 0 11 39Z" fill={foco} stroke={tinta} strokeWidth="1.6" strokeLinejoin="round" />
            </g>
          </g>
          <circle cx="50" cy="18" r="5.5" fill={blanco} {...borde} />
          <circle cx="50" cy="18" r="2" fill={tinta} />
        </g>
      </g>
    </svg>
  );
}
/** @param {{ tam: number }} props */
export const Pelota = ({ tam }) => (
  <svg width={tam} height={tam} viewBox="0 0 60 60" aria-hidden="true">
    <circle cx="30" cy="30" r="28" fill="#FFC43D" />
    <path d="M2.6 33Q30 41 57.4 33L57 39.5Q30 47.5 3 39.5Z" fill="#2F6FDB" />
    <path d="M30 11l3.5 7.2 7.9 1.1-5.7 5.6 1.3 7.9-7-3.7-7 3.7 1.3-7.9-5.7-5.6 7.9-1.1z" fill="#E63946" />
  </svg>
);
/** @param {{ color: string, tam: number }} props */
const Destello = ({ color, tam }) => (
  <svg width={tam} height={tam} viewBox="0 0 24 24" aria-hidden="true">
    <path d="M12 0C13 8 16 11 24 12C16 13 13 16 12 24C11 16 8 13 0 12C8 11 11 8 12 0Z" fill={color} />
  </svg>
);

// ---------- capas animadas (van sobre toda la sección, sin recibir clics) ----------
// Pocas capas animadas y pequeñas: solo algunas estrellas titilan (las demás quedan fijas), los focos titilan por
// grupos y cada fuego artificial se anima completo. Así el navegador mueve pocas capas y el scroll no se traba.

/**
 * Reparte los elementos en grupos: el elemento i va al grupo i % grupos.
 * @template T
 * @param {T[]} lista
 * @param {number} grupos
 * @returns {T[][]}
 */
const repartir = (lista, grupos) => Array.from({ length: grupos }, (_, k) => lista.filter((__, i) => i % grupos === k));

/** Titila una de cada tantas estrellas. */
const TITILA_CADA = 4;
/** Duración y desfase (s) de cada grupo de focos: parpadean por color, como una serie de luces. @type {Array<[number, number]>} */
const RITMO_FOCOS = [[1.8, 0], [2.3, 0.6], [2.9, 1.2]];

/**
 * Cielo de estrellas: una de cada 4 titila; las demás quedan fijas, cada una con su brillo.
 * @param {{ n: number, semilla: number, color?: string, hasta?: number, desde?: number, className?: string }} props
 */
export function Estrellas({ n, semilla, color = '#FFFFFF', hasta = 100, desde = 0, className = '' }) {
  const puntos = useMemo(() => {
    const r = azar(semilla);
    return Array.from({ length: n }, (_, i) => ({ x: r() * 100, y: desde + r() * (hasta - desde), t: 1.5 + r() * 1.7, d: r() * 3, dur: 2.4 + r() * 2.2, titila: i % TITILA_CADA === 0 }));
  }, [n, semilla, hasta, desde]);
  return (
    <div className={`ev-capa ${className}`} aria-hidden="true">
      {puntos.map((p, i) => (
        <span key={i} className={`ev-estrella${p.titila ? ' ev-titila' : ''}`} style={p.titila
          ? { left: `${p.x}%`, top: `${p.y}%`, width: p.t, height: p.t, background: color, animationDelay: `-${p.d}s`, animationDuration: `${p.dur}s` }
          : { left: `${p.x}%`, top: `${p.y}%`, width: p.t, height: p.t, background: color, opacity: 0.3 + (p.d / 3) * 0.6 }} />
      ))}
    </div>
  );
}

/**
 * Fuego artificial: 12 rayos que salen del centro y se apagan (se anima el estallido completo, no cada rayo).
 * @param {{ x: string, y: string, color: string, retraso?: number, largo?: number, className?: string }} props
 */
export function Fuego({ x, y, color, retraso = 0, largo = 46, className = '' }) {
  return (
    <div className={`ev-fuego ${className}`} aria-hidden="true" style={{ left: x, top: y }}>
      <div className="ev-fuego__estallido ev-estalla" style={{ animationDelay: `${retraso}s` }}>
        {Array.from({ length: 12 }, (_, i) => (
          <span key={i} className="ev-fuego__rayo" style={{ height: largo, transform: `rotate(${i * 30}deg)`, background: `linear-gradient(transparent 15%, ${color})` }} />
        ))}
      </div>
    </div>
  );
}

/**
 * Guirnalda de focos que titilan, colgada de lado a lado.
 * @param {{ colores: string[], n: number, caida: number, className?: string }} props
 */
export function Guirnalda({ colores, n, caida, className = '' }) {
  const grupos = useMemo(() => {
    const focos = Array.from({ length: n + 1 }, (_, i) => {
      const t = i / n;
      return { x: t * 100, y: caida * 4 * t * (1 - t), color: colores[i % colores.length] };
    });
    return repartir(focos, RITMO_FOCOS.length);
  }, [colores, n, caida]);
  return (
    <div className={`ev-guirnalda ${className}`} aria-hidden="true" style={{ height: caida + 16 }}>
      <svg viewBox="0 0 100 10" preserveAspectRatio="none" style={{ height: caida }}>
        <path d="M0 0 Q50 20 100 0" vectorEffect="non-scaling-stroke" />
      </svg>
      {grupos.map((focos, k) => (
        <div key={k} className="ev-grupo ev-titila" style={{ animationDuration: `${RITMO_FOCOS[k][0]}s`, animationDelay: `-${RITMO_FOCOS[k][1]}s` }}>
          {focos.map((f) => <span key={f.x} className="ev-foco" style={{ left: `${f.x}%`, top: f.y, background: f.color }} />)}
        </div>
      ))}
    </div>
  );
}

/** Clases de deriva lateral y de giro al caer (definidas en Eventos.css). @type {string[]} */
const DERIVAS = ['ev-dx-a', 'ev-dx-b', 'ev-dx-c', 'ev-dx-d'];
/** @type {string[]} */
const GIROS = ['ev-g-a', 'ev-g-b', 'ev-g-c'];

/**
 * Confeti de Halloween que cae: cuadritos, calabazas, fantasmas y dulces.
 * @param {{ n: number, semilla: number, className?: string }} props
 */
export function Confeti({ n, semilla, className = '' }) {
  const piezas = useMemo(() => {
    const r = azar(semilla);
    const colores = ['#F7931E', '#8E5CD9', '#9BE15D', '#FFD166', '#FFFFFF', '#EF5DA8'];
    return Array.from({ length: n }, (_, i) => ({
      tipo: i % 6, x: r() * 100, y: -2 + r() * 75, dur: 7 + r() * 6, d: r() * 12,
      tam: r(), color: colores[Math.floor(r() * colores.length)], clase: `${DERIVAS[i % 4]} ${GIROS[i % 3]}`
    }));
  }, [n, semilla]);
  return (
    <div className={`ev-capa ev-capa--frente ${className}`} aria-hidden="true">
      {piezas.map((p, i) => {
        let figura;
        if (p.tipo === 0) figura = <Calabaza tam={Math.round(18 + p.tam * 8)} />;
        else if (p.tipo === 1) figura = <Fantasma tam={Math.round(14 + p.tam * 6)} />;
        else if (p.tipo === 2) figura = <Caramelo tam={Math.round(10 + p.tam * 4)} />;
        else if (p.tipo === 3) figura = <span className="ev-papelito" style={{ width: Math.round(7 + p.tam * 3), height: Math.round(12 + p.tam * 4), background: p.color }} />;
        else figura = <span className="ev-papelito ev-papelito--redondo" style={{ width: Math.round(8 + p.tam * 3), height: Math.round(8 + p.tam * 3), background: p.color }} />;
        return <div key={i} className={`ev-cae ${p.clase}`} style={{ left: `${p.x}%`, top: `${p.y}%`, animationDuration: `${p.dur}s`, animationDelay: `-${p.d}s` }}>{figura}</div>;
      })}
    </div>
  );
}

/**
 * Nieve que cae.
 * @param {{ n: number, semilla: number, className?: string }} props
 */
export function Nieve({ n, semilla, className = '' }) {
  const copos = useMemo(() => {
    const r = azar(semilla);
    return Array.from({ length: n }, (_, i) => ({ x: r() * 100, y: -2 + r() * 80, t: Math.round(3 + r() * 5), o: 0.55 + r() * 0.4, dur: 9 + r() * 8, d: r() * 16, clase: DERIVAS[i % 4] }));
  }, [n, semilla]);
  return (
    <div className={`ev-capa ev-capa--frente ${className}`} aria-hidden="true">
      {copos.map((c, i) => <span key={i} className={`ev-copo ev-nieve ${c.clase}`} style={{ left: `${c.x}%`, top: `${c.y}%`, width: c.t, height: c.t, background: `rgba(255, 255, 255, ${c.o.toFixed(2)})`, animationDuration: `${c.dur}s`, animationDelay: `-${c.d}s` }} />)}
    </div>
  );
}

/**
 * Niebla verde que sube del suelo.
 * @param {{ n: number, semilla: number, className?: string }} props
 */
export function Niebla({ n, semilla, className = '' }) {
  const nubes = useMemo(() => {
    const r = azar(semilla);
    return Array.from({ length: n }, () => ({ x: -6 + r() * 86, w: 260 + r() * 180, dur: 8 + r() * 4, d: r() * 10 }));
  }, [n, semilla]);
  return (
    <div className={`ev-capa ${className}`} aria-hidden="true">
      {nubes.map((b, i) => <span key={i} className="ev-niebla ev-sube" style={{ left: `${b.x}%`, width: b.w, height: Math.round(b.w * 0.55), animationDuration: `${b.dur}s`, animationDelay: `-${b.d}s` }} />)}
    </div>
  );
}

/**
 * Murciélagos que cruzan la sección.
 * @param {{ n: number, semilla: number, className?: string }} props
 */
export function Murcielagos({ n, semilla, className = '' }) {
  const vuelos = useMemo(() => {
    const r = azar(semilla);
    return Array.from({ length: n }, () => ({ y: 9 + r() * 46, escala: 0.55 + r() * 0.5, dur: 13 + r() * 7, d: r() * 18 }));
  }, [n, semilla]);
  return (
    <div className={`ev-capa ${className}`} aria-hidden="true">
      {vuelos.map((v, i) => (
        <div key={i} className="ev-murcielago ev-vuela" style={{ top: `${v.y}%`, animationDuration: `${v.dur}s`, animationDelay: `-${v.d}s` }}>
          <div style={{ transform: `scale(${v.escala.toFixed(2)})` }}><Murcielago /></div>
        </div>
      ))}
    </div>
  );
}

/**
 * Destellos dorados en puntos fijos ([x %, y %, tamaño px]).
 * @param {{ puntos: Array<[number, number, number]>, semilla: number, color?: string, className?: string }} props
 */
export function Destellos({ puntos, semilla, color = '#F2C14E', className = '' }) {
  const retrasos = useMemo(() => {
    const r = azar(semilla);
    return puntos.map(() => r() * 2.8);
  }, [puntos, semilla]);
  return (
    <div className={`ev-capa ev-capa--frente ${className}`} aria-hidden="true">
      {puntos.map(([x, y, t], i) => <span key={i} className="ev-destello ev-brillo" style={{ left: `${x}%`, top: `${y}%`, width: t, height: t, animationDelay: `-${retrasos[i].toFixed(2)}s` }}><Destello color={color} tam={t} /></span>)}
    </div>
  );
}
